import {
  DenimUploadBatch,
  DenimUploadBatchStatus,
  FactoredInvoiceBase,
} from "../../../../app/data/invoice-factoring/models";

export type BatchAction = "start" | "stop" | "resume" | "retryFailed";

export type NextActionTone = "action" | "progress" | "done" | "warning";

export interface NextAction {
  label: string;
  tone: NextActionTone;
}

type BatchCounters = Pick<
  DenimUploadBatch,
  "status" | "totalCount" | "processedCount" | "failedCount" | "invalidCount"
>;

/** Rows that still need to be sent: waiting or interrupted mid-send. */
export const getRemainingCount = (batch: BatchCounters) =>
  Math.max(batch.totalCount - batch.invalidCount - batch.processedCount, 0);

export const getProgressPercent = (batch: BatchCounters) => {
  const sendable = batch.totalCount - batch.invalidCount;
  if (sendable <= 0) return 100;
  return Math.min(100, Math.round((batch.processedCount / sendable) * 100));
};

const RESUMABLE: DenimUploadBatchStatus[] = ["STOPPED", "FAILED"];
const RETRYABLE: DenimUploadBatchStatus[] = ["COMPLETED_WITH_ERRORS", "STOPPED", "FAILED"];

/** Mirrors the backend lifecycle guards so only actions that will succeed are offered. */
export const getAvailableBatchActions = (batch: BatchCounters): BatchAction[] => {
  const actions: BatchAction[] = [];
  const remaining = getRemainingCount(batch);
  if (batch.status === "DRAFT" && remaining > 0) actions.push("start");
  if (batch.status === "DRAFT" || batch.status === "QUEUED" || batch.status === "RUNNING") actions.push("stop");
  if (RESUMABLE.includes(batch.status) && remaining > 0) actions.push("resume");
  if (RETRYABLE.includes(batch.status) && batch.failedCount > 0) actions.push("retryFailed");
  return actions;
};

export const getBatchNextAction = (batch: BatchCounters): NextAction => {
  const remaining = getRemainingCount(batch);
  switch (batch.status) {
    case "DRAFT":
      return remaining > 0
        ? { label: "Start sending", tone: "action" }
        : { label: "No valid rows to send", tone: "warning" };
    case "QUEUED":
      return { label: "Waiting to start", tone: "progress" };
    case "RUNNING":
      return { label: "Sending", tone: "progress" };
    case "STOP_REQUESTED":
      return { label: "Stopping after current invoice", tone: "progress" };
    case "STOPPED":
      if (remaining > 0) return { label: "Resume to send remaining", tone: "action" };
      if (batch.failedCount > 0) return { label: "Retry failed", tone: "action" };
      return { label: "No action needed", tone: "done" };
    case "COMPLETED":
      return { label: "No action needed", tone: "done" };
    case "COMPLETED_WITH_ERRORS":
      return { label: "Review failed invoices", tone: "warning" };
    case "FAILED":
      return remaining > 0
        ? { label: "Resume batch", tone: "warning" }
        : { label: "Retry failed", tone: "warning" };
    default:
      return { label: "—", tone: "done" };
  }
};

type InvoiceState = Pick<FactoredInvoiceBase, "status" | "statusReason">;

export const getInvoiceNextAction = (invoice: InvoiceState): NextAction => {
  switch (invoice.status) {
    case "PENDING":
      return { label: "Send in a batch", tone: "action" };
    case "JOB_CREATED":
      return { label: "Send documents in a batch", tone: "action" };
    case "DOCUMENTS_SENT":
      return { label: "Track on Denim", tone: "done" };
    case "NEEDS_ATTENTION":
      return { label: invoice.statusReason || "Review the latest attempt", tone: "warning" };
    default:
      return { label: "—", tone: "done" };
  }
};
